import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { authService } from '../../services/api';
import { Box, Button, Typography, Container, Paper, TextField, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { userService } from '../../services/api';

const ProfileDetail = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState('user');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const res = await authService.getProfile();
        setUser(res.data);
        setFormData({
          first_name: res.data.first_name || '',
          last_name: res.data.last_name || '',
          email: res.data.email || '',
          phone_number: res.data.phone_number || '',
          city: res.data.city || '',
          address: res.data.address || '',
          street_number: res.data.street_number || '',
          fiscal_code: res.data.fiscal_code || ''
        });
      } catch (e) {
        setError('Errore nel caricamento del profilo');
        console.error(e);
      }
      setLoading(false);
    };
    loadProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      const res = await api.patch('/users/me/', formData);
      setUser(res.data);
      setSuccessMessage('Dati profilo aggiornati con successo.');
    } catch (e) {
      setError('Errore nel salvataggio dei dati');
      console.error(e);
    }
    setSaving(false);
  };

  const openRoleDialog = () => {
    setNewRole(user?.role || 'user');
    setDialogOpen(true);
  };

  const closeRoleDialog = () => setDialogOpen(false);

  const confirmRoleChange = async () => {
    const id = user?.id;
    if (!id) return;
    // Consentito a admin, superadmin; moderatore solo se target è user
    if (!(currentUser?.role === 'admin' || currentUser?.role === 'superadmin' || (currentUser?.role === 'moderator' && user?.role === 'user'))) {
      closeRoleDialog();
      return;
    }
    if (newRole === user.role) {
      closeRoleDialog();
      return;
    }
    setActionInProgress(true);
    setError('');
    setSuccessMessage('');
    try {
      await userService.updateUser(id, { role: newRole });
      setUser(prev => ({ ...prev, role: newRole }));
      setSuccessMessage('Ruolo aggiornato con successo.');
      closeRoleDialog();
    } catch (e) {
      setError('Errore nell\'aggiornamento del ruolo');
      console.error(e);
    }
    setActionInProgress(false);
  };

  const handleResetPassword = async () => {
    const id = user?.id;
    if (!id) return;
    if (!(currentUser?.role === 'admin' || currentUser?.role === 'superadmin')) return;
    setResetLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      await userService.resetPassword(id);
      setSuccessMessage('Password temporanea generata e inviata via email.');
    } catch (e) {
      setError('Errore nel reset della password');
      console.error(e);
    }
    setResetLoading(false);
  };

  const handleToggleActive = async () => {
    const id = user?.id;
    if (!id) return;
    // I moderatori non possono modificare admin; per il proprio profilo vale la policy backend
    setActionInProgress(true);
    setError('');
    setSuccessMessage('');
    try {
      await userService.updateUser(id, { is_active: !user.is_active });
      setUser(prev => ({ ...prev, is_active: !prev.is_active }));
      setSuccessMessage(`Profilo ${!user.is_active ? 'attivato' : 'disattivato'} con successo.`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || 'Errore nell\'aggiornamento dello stato');
      console.error(e);
    }
    setActionInProgress(false);
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container component="main" maxWidth="md" sx={{ mt: 8 }}>
        <Alert severity="error">Profilo non trovato.</Alert>
      </Container>
    );
  }

  const isUserOnly = currentUser?.role === 'user';
  const canShowRoleButton = (currentUser?.role === 'admin' || currentUser?.role === 'superadmin' || (currentUser?.role === 'moderator' && user.role === 'user'));
  const canShowResetButton = (currentUser?.role === 'admin' || currentUser?.role === 'superadmin');
  const canShowToggleActive = (currentUser?.role === 'admin' || currentUser?.role === 'superadmin' || currentUser?.role === 'moderator');

  return (
    <Container component="main" maxWidth="false">
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 4, md: 8 } }}>
        <Typography component="h1" variant="h4" gutterBottom align="center">
          Dettaglio Utente
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <TextField label="Nome" name="first_name" value={formData.first_name} onChange={handleChange} fullWidth />
          <TextField label="Cognome" name="last_name" value={formData.last_name} onChange={handleChange} fullWidth />
          <TextField label="Email" name="email" value={formData.email} onChange={handleChange} fullWidth />
          <TextField label="Cellulare" name="phone_number" value={formData.phone_number} onChange={handleChange} fullWidth />
          <TextField label="Città" name="city" value={formData.city} onChange={handleChange} fullWidth />
          <TextField label="Indirizzo" name="address" value={formData.address} onChange={handleChange} fullWidth />
          <TextField label="Numero Civico" name="street_number" value={formData.street_number} onChange={handleChange} fullWidth />
          <TextField label="Codice Fiscale" name="fiscal_code" value={formData.fiscal_code} onChange={handleChange} fullWidth />
        </Box>

        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
          {canShowRoleButton && (
            <Button variant="contained" color="secondary" onClick={openRoleDialog} disabled={actionInProgress}>
              Cambia Ruolo
            </Button>
          )}
          {canShowResetButton && (
            <Button variant="outlined" color="warning" onClick={handleResetPassword} disabled={resetLoading}>
              {resetLoading ? 'Invio...' : 'Reset Password'}
            </Button>
          )}
          <Button variant="outlined" color="primary" onClick={() => navigate('/profile/skills')}>
            Gestisci Abilità
          </Button>
          {canShowToggleActive && (
            <Button variant="contained" color={user.is_active ? 'error' : 'success'} onClick={handleToggleActive} disabled={actionInProgress}>
              {user.is_active ? 'Disattiva' : 'Attiva'}
            </Button>
          )}
          {!isUserOnly && (
            <Button variant="outlined" onClick={() => navigate('/admin/users')}>Torna alla lista</Button>
          )}
        </Box>

        {/* Dialog cambio ruolo */}
        <Dialog open={dialogOpen} onClose={closeRoleDialog}>
          <DialogTitle>Cambia Ruolo Utente</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Seleziona il nuovo ruolo per il tuo profilo:
            </DialogContentText>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel id="role-select-label">Ruolo</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={newRole}
                label="Ruolo"
                onChange={(e) => setNewRole(e.target.value)}
              >
                {currentUser?.role === 'superadmin' && <MenuItem value="superadmin">Super Amministratore</MenuItem>}
                {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                  <MenuItem value="admin">Amministratore</MenuItem>
                )}
                {currentUser?.role === 'superadmin' && (
                  <MenuItem value="moderator">Moderatore</MenuItem>
                )}
                {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin' || currentUser?.role === 'moderator') && (
                  <MenuItem value="user">Utente</MenuItem>
                )}
                {currentUser?.role === 'moderator' && (
                  <MenuItem value="moderator">Moderatore</MenuItem>
                )}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeRoleDialog}>Annulla</Button>
            <Button onClick={confirmRoleChange} variant="contained" color="primary">Conferma</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default ProfileDetail;
